import { useLayoutEffect, useRef, useState, type MutableRefObject } from 'react';
import { createPortal } from 'react-dom';

/** Отступ от края окна, ближе которого пузырь не подходит. */
const VIEWPORT_MARGIN = 8;

/** Высота пузыря с отступом — по ней решаем, влезает ли он над элементом. */
const TOOLTIP_HEIGHT = 36;

/**
 * Кастомный тултип вместо нативного title/CSS-only [data-tooltip]::after —
 * тот clip'ился оверфлоу-контейнерами (напр. .table-wrap { overflow-x: auto }
 * обрезает и вертикальный оверфлоу по спецификации CSS, тултип у заголовков
 * таблицы уходил за границу и не был виден). position: fixed + портал в body
 * не зависит от overflow родителей.
 *
 * Координаты зажимаются в границы окна ПО РЕАЛЬНОЙ ширине пузыря, измеренной
 * после рендера: раньше половина ширины предполагалась константой (56px), и
 * подписи длиннее этого (напр. «Удалить шаблон» у кнопки в правом верхнем
 * углу) всё равно уезжали за край экрана. useLayoutEffect правит позицию до
 * отрисовки, поэтому скачка не видно.
 */
export function useTooltip<T extends HTMLElement = HTMLElement>(label: string | undefined) {
  const [coords, setCoords] = useState<{ top: number; left: number; below: boolean } | null>(null);
  const ref = useRef<T>(null) as MutableRefObject<T | null>;
  const bubbleRef = useRef<HTMLSpanElement>(null);

  const show = () => {
    if (!label || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    // Над элементом места нет (шапка прижата к верху окна) — показываем под ним,
    // иначе пузырь уезжает за верхний край и его просто не видно.
    const below = rect.top < TOOLTIP_HEIGHT + VIEWPORT_MARGIN;

    setCoords({
      top: below ? rect.bottom + 8 : rect.top - 8,
      left: rect.left + rect.width / 2,
      below,
    });
  };

  const hide = () => setCoords(null);

  useLayoutEffect(() => {
    if (!coords || !bubbleRef.current) return;
    const half = bubbleRef.current.getBoundingClientRect().width / 2;
    // clientWidth, а НЕ window.innerWidth: последний включает ширину
    // вертикальной полосы прокрутки, и тултип у элемента в правом верхнем
    // углу заезжал под неё (визуально «немного обрезан справа»).
    const viewportWidth = document.documentElement.clientWidth;
    const min = half + VIEWPORT_MARGIN;
    const max = viewportWidth - half - VIEWPORT_MARGIN;
    // Пузырь шире окна — прижимаем к левому краю, иначе min > max и
    // clamp дал бы координату за пределами экрана.
    const clamped = min > max ? min : Math.min(Math.max(coords.left, min), max);
    if (clamped !== coords.left) setCoords({ ...coords, left: clamped });
  }, [coords]);

  const node =
    label && coords
      ? createPortal(
          <span
            ref={bubbleRef}
            className={`tooltip-bubble${coords.below ? ' tooltip-bubble--below' : ''}`}
            style={{ top: coords.top, left: coords.left }}
          >
            {label}
          </span>,
          document.body,
        )
      : null;

  return {
    ref,
    node,
    handlers: {
      onMouseEnter: show,
      onMouseLeave: hide,
      onFocus: show,
      onBlur: hide,
    },
  };
}
